import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import {
  tokens,
  formatIcd10,
  SectionPanel,
  StatusPill,
  CodeChip,
  ClinicalEmptyState,
} from "../../shared/clinical-design";
import {
  NOTE_STATUS,
  NOTE_STATUS_META,
  ISSUE_TYPE_LABELS,
  ISSUE_SEVERITY_META,
} from "../common/constants";

/**
 * Detalle de una nota con hallazgos: excerpt, issues con severidad,
 * sugerencia de código (before → after) y tips de coaching.
 *
 * @param {Object} note - nota seleccionada (shape de fakeData)
 * @param {Function} onSendCoaching - (noteId) => void
 * @param {boolean} sending
 */
export default function NoteReviewDetail({ note, onSendCoaching, sending = false }) {
  if (!note) {
    return (
      <SectionPanel title="Note review">
        <ClinicalEmptyState message="Select a note from the queue to review its documentation findings." />
      </SectionPanel>
    );
  }

  const statusMeta = NOTE_STATUS_META[note.status];

  return (
    <Stack spacing={1.5}>
      <SectionPanel
        title={`${note.patientName} · ${note.noteType}`}
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <StatusPill tone={statusMeta.tone} label={statusMeta.label} dense />
            {note.status === NOTE_STATUS.OPEN && (
              <Button
                size="small"
                variant="outlined"
                disabled={sending}
                onClick={() => onSendCoaching(note.id)}
                sx={{
                  textTransform: "none",
                  fontSize: 12.5,
                  color: tokens.primary,
                  borderColor: tokens.primary,
                  "&:hover": { borderColor: tokens.primary700, bgcolor: tokens.primary50 },
                }}
              >
                {sending ? "Sending..." : "Send coaching"}
              </Button>
            )}
          </Stack>
        }
        contentSx={{ pt: 1.5 }}
      >
        <Stack direction="row" spacing={1.5} sx={{ mb: 1, flexWrap: "wrap", rowGap: 0.5 }}>
          <CodeChip code={note.cipId} />
          <Typography sx={{ fontSize: 12.5, color: tokens.ink2 }}>
            {note.provider} · signed {note.noteDate}
          </Typography>
          <Typography
            sx={{
              fontFamily: tokens.fontMono,
              fontSize: 12.5,
              color:
                note.docScore >= 80 ? tokens.ok : note.docScore >= 65 ? tokens.warn : tokens.crit,
            }}
          >
            Doc score {note.docScore}/100
          </Typography>
        </Stack>
        <Box
          sx={{
            bgcolor: tokens.surface2,
            border: `1px solid ${tokens.line}`,
            borderLeft: `3px solid ${tokens.lineStrong}`,
            borderRadius: "4px",
            px: 1.5,
            py: 1.2,
          }}
        >
          <Typography
            sx={{
              fontFamily: tokens.fontMono,
              fontSize: 12.5,
              color: tokens.ink2,
              whiteSpace: "pre-wrap",
              lineHeight: 1.6,
            }}
          >
            {note.excerpt}
          </Typography>
        </Box>
      </SectionPanel>

      <SectionPanel title={`Detected issues (${note.issues.length})`} contentSx={{ pt: 1, pb: 1 }}>
        {note.issues.length === 0 && (
          <ClinicalEmptyState message="No documentation issues — this note meets the quality bar." sx={{ py: 1 }} />
        )}
        {note.issues.map((issue, i) => {
          const sev = ISSUE_SEVERITY_META[issue.severity] || ISSUE_SEVERITY_META.low;
          return (
            <Stack
              key={`${issue.type}-${i}`}
              direction="row"
              alignItems="flex-start"
              spacing={1}
              sx={{ py: 0.7, borderBottom: `1px solid ${tokens.line}`, "&:last-of-type": { border: 0 } }}
            >
              <StatusPill tone={sev.tone} label={sev.label} dense />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: tokens.ink2 }}>
                  {ISSUE_TYPE_LABELS[issue.type] || issue.type}
                </Typography>
                <Typography sx={{ fontSize: 13, color: tokens.ink }}>{issue.description}</Typography>
              </Box>
            </Stack>
          );
        })}
      </SectionPanel>

      {note.suggestion && (
        <SectionPanel title="Suggested specificity" contentSx={{ pt: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: "wrap", rowGap: 1 }}>
            <Stack spacing={0.3} alignItems="flex-start">
              <CodeChip
                code={note.suggestion.fromCode === "—" ? "—" : formatIcd10(note.suggestion.fromCode)}
                raw={note.suggestion.fromCode}
                tone="warn"
              />
              <Typography sx={{ fontSize: 11.5, color: tokens.ink3 }}>
                {note.suggestion.fromDesc}
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: 16, color: tokens.ink3 }}>→</Typography>
            <Stack spacing={0.3} alignItems="flex-start">
              <CodeChip code={formatIcd10(note.suggestion.toCode)} raw={note.suggestion.toCode} tone="ok" />
              <Typography sx={{ fontSize: 11.5, color: tokens.ink3 }}>
                {note.suggestion.toDesc}
              </Typography>
            </Stack>
          </Stack>
          <Typography sx={{ fontSize: 13, color: tokens.ink2, mt: 1.2 }}>
            {note.suggestion.rationale}
          </Typography>
        </SectionPanel>
      )}

      {note.coachingTips.length > 0 && (
        <SectionPanel title="Coaching tips" contentSx={{ pt: 1, pb: 1 }}>
          {note.coachingTips.map((tip, i) => (
            <Stack key={tip} direction="row" spacing={1} sx={{ py: 0.5 }}>
              <Typography
                sx={{ fontFamily: tokens.fontMono, fontSize: 12.5, color: tokens.primary, minWidth: 20 }}
              >
                {i + 1}.
              </Typography>
              <Typography sx={{ fontSize: 13, color: tokens.ink }}>{tip}</Typography>
            </Stack>
          ))}
        </SectionPanel>
      )}
    </Stack>
  );
}
